import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import Webcam from "react-webcam";
import DefaultInput from "../../../../../../common/components/default_input";
import { FaceDetection, Results } from "@mediapipe/face_detection";
import { Camera } from "@mediapipe/camera_utils";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedBiometriaFacial: any | null;
  funcionario: any | undefined;
  ddlTermos: any[];
  onSubmit: (form: any) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: any) => void;
  onClose: (idBiometriaFacial: number | null, openState: boolean) => void;
};

export const CadastroBiometriaFacialModal = ({
  open,
  saving,
  loading,
  selectedBiometriaFacial,
  funcionario,
  ddlTermos,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const [biometriaFacial, setBiometriaFacial] = useState<any | null>(
    selectedBiometriaFacial,
  );

  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [apiMessage, setApiMessage] = useState({ type: "", text: "" });

  const [termoLido, setTermoLido] = useState<boolean>(false);
  const [modalLeituraAberto, setModalLeituraAberto] = useState<boolean>(false);

  const [instrucao, setInstrucao] = useState("Posicione seu rosto na câmera");
  const [podeCadastrar, setPodeCadastrar] = useState(false);

  const hookForm = useForm<any>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const idTermoSelecionado = hookForm.watch("idTermoEpi");
  const termoSelecionadoObj = ddlTermos?.find(
    (x) => x.idTermoEPI === idTermoSelecionado,
  );

  const temBiometria = hookForm.watch("biometria");

  const stopCamera = useCallback(() => {
    setIsCameraOpen(false);
    setApiMessage({ type: "", text: "" });
    setPodeCadastrar(false);
  }, []);

  const startCamera = () => {
    setApiMessage({ type: "", text: "" });
    setInstrucao("Posicione seu rosto na câmera");
    setPodeCadastrar(false);
    setIsCameraOpen(true);
  };

  useEffect(() => {
    if (idTermoSelecionado && open) {
      setTermoLido(false);
      setPodeCadastrar(false);
      setModalLeituraAberto(true);
    } else {
      setTermoLido(false);
    }
  }, [idTermoSelecionado, open]);

  useEffect(() => {
    if (!isCameraOpen) return;

    let isActive = true;
    let camera: Camera | null = null;
    let faceDetection: FaceDetection | null = null;
    let timeoutId: ReturnType<typeof setTimeout>;

    const inicializarIA = async () => {
      if (!isActive) return;

      const videoElement = webcamRef.current?.video;
      if (!videoElement) return;

      faceDetection = new FaceDetection({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
      });

      faceDetection.setOptions({
        model: "short",
        minDetectionConfidence: 0.5,
      });

      faceDetection.onResults((results: Results) => {
        if (!isActive) return;

        const canvasElement = canvasRef.current;
        const canvasCtx = canvasElement?.getContext("2d");

        if (!canvasElement || !canvasCtx || !videoElement) return;

        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;
        canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);

        if (results.detections.length > 0) {
          const deteccao = results.detections[0];
          const boundingBox = deteccao.boundingBox;
          const landmarks = deteccao.landmarks;

          const proporcaoRosto = boundingBox.width * boundingBox.height;
          let isFaceGood = false;

          if (proporcaoRosto < 0.18) {
            setInstrucao("Aproxime mais o seu rosto");
            setPodeCadastrar(false);
          } else if (proporcaoRosto > 0.6) {
            setInstrucao("Afaste um pouco o rosto");
            setPodeCadastrar(false);
          } else {
            let isLookingStraight = true;

            if (landmarks && landmarks.length >= 4) {
              const rightEye = landmarks[0];
              const leftEye = landmarks[1];
              const nose = landmarks[2];
              const mouth = landmarks[3];

              const eyeDeltaY = Math.abs(rightEye.y - leftEye.y);
              const eyeDeltaX = Math.abs(rightEye.x - leftEye.x) || 0.001;
              const eyeTilt = eyeDeltaY / eyeDeltaX;

              const eyeCenterX = (rightEye.x + leftEye.x) / 2;
              const noseDeviation = Math.abs(nose.x - eyeCenterX) / eyeDeltaX;

              const eyeCenterY = (rightEye.y + leftEye.y) / 2;
              const eyeToNose = nose.y - eyeCenterY;
              const noseToMouth = mouth.y - nose.y || 0.001;
              const verticalRatio = eyeToNose / noseToMouth;

              if (
                eyeTilt > 0.25 ||
                noseDeviation > 0.3 ||
                verticalRatio < 0.6 ||
                verticalRatio > 2.0
              ) {
                isLookingStraight = false;
              }
            }

            if (!isLookingStraight) {
              setInstrucao("Olhe diretamente para a câmera");
              setPodeCadastrar(false);
            } else {
              setInstrucao("A face está com boa qualidade");
              setPodeCadastrar(true);
              isFaceGood = true;
            }
          }

          const largura = boundingBox.width * canvasElement.width;
          const altura = boundingBox.height * canvasElement.height;
          const x = boundingBox.xCenter * canvasElement.width - largura / 2;
          const y = boundingBox.yCenter * canvasElement.height - altura / 2;

          canvasCtx.beginPath();
          canvasCtx.lineWidth = 4;
          canvasCtx.strokeStyle = isFaceGood ? "#84BD42" : "#e50914";
          canvasCtx.setLineDash([10, 10]);
          canvasCtx.rect(x, y, largura, altura);
          canvasCtx.stroke();
          canvasCtx.setLineDash([]);
        } else {
          setInstrucao("Nenhum rosto detectado");
          setPodeCadastrar(false);
        }
      });

      camera = new Camera(videoElement, {
        onFrame: async () => {
          if (!isActive || !faceDetection) return;

          try {
            await faceDetection.send({ image: videoElement });
          } catch (error) {
            console.warn("Frame ignorado na desmontagem do componente.");
          }
        },
        width: 1280,
        height: 720,
      });

      if (isActive) camera.start();
    };

    timeoutId = setTimeout(inicializarIA, 600);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);

      if (camera) camera.stop();
      if (faceDetection) {
        try {
          faceDetection.close();
        } catch (e) { }
      }
    };
  }, [isCameraOpen]);

  const handleCaptureAndRegister = async () => {
    if (!podeCadastrar) return;

    if (!funcionario) {
      setApiMessage({
        type: "error",
        text: "Selecione um funcionário antes de capturar a face!",
      });
      return;
    }

    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      setApiMessage({
        type: "error",
        text: "Não foi possível acessar a captura da câmera.",
      });
      return;
    }

    const base64Image = imageSrc.split(",")[1];

    // Guarda a imagem no hook form
    hookForm.setValue("biometria", base64Image, { shouldDirty: true });

    // Fecha a câmera e indica sucesso visual
    setIsCameraOpen(false);
    setApiMessage({
      type: "success",
      text: 'Face capturada com sucesso! Clique em "Salvar" para confirmar e assinar.',
    });
  };

  useEffect(() => {
    if (open) {
      setTermoLido(false);
      setModalLeituraAberto(false);
      hookForm.setValue("idTermoEpi", null as any);

      if (selectedBiometriaFacial) {
        hookForm.reset({
          idBiometriaFacial: selectedBiometriaFacial.idBiometriaFacial,
          idFuncionario: selectedBiometriaFacial.idFuncionario,
          data: selectedBiometriaFacial.data,
          hora: selectedBiometriaFacial.hora,
          biometria: selectedBiometriaFacial.biometria,
          gryfo_request_id: selectedBiometriaFacial.gryfo_request_id,
          idTermoEpi: selectedBiometriaFacial.idTermoEpi || null,
        });

        if (selectedBiometriaFacial.biometria) setTermoLido(true);
      } else {
        const now = new Date();
        hookForm.reset({
          idBiometriaFacial: 0,
          idFuncionario: null,
          data: now,
          hora: now.toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          biometria: null,
          gryfo_request_id: "Aguardando Captura...",
          idTermoEpi: null,
        });
      }
      setBiometriaFacial(selectedBiometriaFacial ?? null);
    } else {
      stopCamera();
    }
  }, [hookForm, open, selectedBiometriaFacial, stopCamera]);

  const handleClose = useCallback(() => {
    if (!saving) {
      stopCamera();
      onClose(null, false);
    }
  }, [saving, onClose, stopCamera]);

  return (
    <>
      <Dialog
        open={open}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: hookForm.handleSubmit(onSubmit),
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "45rem",
            },
          },
        }}
      >
        <DialogTitle>
          {selectedBiometriaFacial ? "Editar" : "Cadastrar"} Biometria Facial
        </DialogTitle>

        <DialogContent>
          <ModalLoading loading={saving || loading}>
            <FormContainer title={"Informações"} arrangement="fullRow">
              <div className="flex flex-col gap-4">
                <DefaultInput
                  name="Nome"
                  label="Nome do funcionario"
                  readOnly={true}
                  value={funcionario?.nome}
                />

                <div className="flex flex-row gap-4 w-full">
                  <div className="w-1/2">
                    <FormDatePickerInput
                      name="data"
                      label="Data"
                      readOnly={true}
                      reactHookForm={hookForm}
                    />
                  </div>
                  <div className="w-1/2">
                    <DefaultFormInput
                      name="hora"
                      label="Hora"
                      readOnly={true}
                      reactHookForm={hookForm}
                    />
                  </div>
                </div>

                <DefaultFormAutocomplete
                  name="idTermoEpi"
                  label="Selecione o Termo"
                  required={true}
                  options={ddlTermos?.filter((x) => x.ativo === true) || []}
                  getOptionKey={(x: any) => x.idTermoEPI}
                  getOptionLabel={(x: any) => x.nome ?? ""}
                  reactHookForm={hookForm}
                />

                <div className="flex flex-col gap-2 p-4 mt-2 border border-gray-200 dark:border-gray-700 rounded bg-gray-50/50 dark:bg-transparent items-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-full text-left mb-2">
                    Captura de Face (Webcam)
                  </span>

                  <Controller
                    name="biometria"
                    control={hookForm.control}
                    render={({ field }) => (
                      <div className="w-full flex flex-col items-center gap-4">
                        {field.value ? (
                          <div className="flex flex-col items-center gap-3 w-full">
                            <img
                              src={`data:image/jpeg;base64,${field.value}`}
                              alt="Biometria Validada"
                              className="w-full max-w-[320px] rounded-lg border-4 border-primary-green shadow-md"
                            />
                            <PrimaryButton
                              disabled={saving || loading}
                              onClick={() => {
                                hookForm.setValue("biometria", null, {
                                  shouldDirty: true,
                                });
                                hookForm.setValue(
                                  "gryfo_request_id",
                                  "Aguardando Captura...",
                                  { shouldDirty: true },
                                );
                              }}
                            >
                              Excluir e Tirar Outra
                            </PrimaryButton>
                          </div>
                        ) : isCameraOpen ? (
                          <div className="flex flex-col items-center gap-3 w-full">
                            <div className="bg-amber-100 border border-amber-400 text-amber-800 text-xs px-3 py-2 rounded-md w-full max-w-[400px] text-center shadow-sm">
                              <span className="font-bold">⚠️ Atenção:</span>{" "}
                              Retire <strong>óculos escuros</strong>,{" "}
                              <strong>bonés</strong> ou{" "}
                              <strong>máscaras</strong>.
                            </div>

                            <div className="text-center">
                              <p className="text-xs italic font-semibold text-gray-500">
                                instruções:
                              </p>
                              <h4 className="text-base font-bold text-blue-800">
                                {instrucao}
                              </h4>
                            </div>

                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                maxWidth: 400,
                              }}
                            >
                              <Webcam
                                ref={webcamRef}
                                audio={false}
                                screenshotFormat="image/jpeg"
                                screenshotQuality={1}
                                forceScreenshotSourceSize={true}
                                width={1280}
                                height={720}
                                videoConstraints={{
                                  width: 1280,
                                  height: 720,
                                  facingMode: "user",
                                }}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  borderRadius: 8,
                                }}
                              />
                              <canvas
                                ref={canvasRef}
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  pointerEvents: "none",
                                  zIndex: 10,
                                }}
                              />
                            </div>

                            <div className="flex flex-row gap-2 mt-2">
                              <PrimaryButton
                                buttonColor="primary-green"
                                onClick={handleCaptureAndRegister}
                                disabled={!podeCadastrar}
                              >
                                {podeCadastrar
                                  ? "Capturar Imagem"
                                  : "Aguardando foco..."}
                              </PrimaryButton>
                              <PrimaryButton onClick={stopCamera}>
                                Cancelar
                              </PrimaryButton>
                            </div>
                          </div>
                        ) : !termoLido ? (
                          <div className="w-full text-center py-6 text-gray-500 italic">
                            {idTermoSelecionado
                              ? "Por favor, leia e aceite o termo para habilitar a câmera."
                              : "Selecione um termo acima para continuar."}
                          </div>
                        ) : (
                          <div className="w-full flex justify-center py-4">
                            <PrimaryButton
                              disabled={saving || loading}
                              onClick={startCamera}
                              buttonColor="primary-green"
                            >
                              Ligar Câmera
                            </PrimaryButton>
                          </div>
                        )}

                        {apiMessage.text && (
                          <div
                            className={`w-full max-w-[400px] p-3 rounded text-sm font-semibold text-center ${apiMessage.type === "error"
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                              }`}
                          >
                            {apiMessage.text}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </FormContainer>
          </ModalLoading>
        </DialogContent>

        <DialogActions>
          <div className="w-full h-full items-center justify-end flex p-4 gap-4">
            <div className="flex flex-row gap-2 items-center">
              <div
                className="p-2 hover:bg-gray-100 cursor-pointer dark:hover:bg-gray-800 rounded-[4px] font-semibold"
                onClick={handleClose}
              >
                <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
              </div>

              <div className="w-[120px]">
                {saving ? (
                  <PrimaryButton buttonColor="primary-green">
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    buttonColor="primary-green"
                    disabled={!termoLido || !temBiometria}
                  >
                    Salvar
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalLeituraAberto}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setModalLeituraAberto(false);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle className="bg-gray-50 border-b">
          Leitura Obrigatória
        </DialogTitle>
        <DialogContent className="py-6">
          <div className="flex flex-col gap-4 mt-2">
            <h2 className="font-bold text-lg text-gray-800">
              {termoSelecionadoObj?.nome}
            </h2>
            <div className="bg-gray-100 p-4 rounded text-sm text-gray-700 h-64 overflow-y-auto border border-gray-300">
              <p>{termoSelecionadoObj?.descricao}</p>
            </div>
            <p className="text-xs text-red-500 font-semibold italic text-center">
              Você deve confirmar a leitura para liberar a assinatura
              biométrica.
            </p>
          </div>
        </DialogContent>
        <DialogActions className="p-4 border-t bg-gray-50">
          <Button
            onClick={() => {
              setModalLeituraAberto(false);
              hookForm.setValue("idTermoEpi", null as any);
            }}
            color="inherit"
          >
            Cancelar
          </Button>
          <PrimaryButton
            buttonColor="primary-green"
            onClick={() => {
              setTermoLido(true);
              setModalLeituraAberto(false);
              startCamera();
            }}
          >
            Li e Aceito
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
